import _AccountCircleOutlined from "@mui/icons-material/AccountCircleOutlined";
import _FormControlLabel from "@mui/material/FormControlLabel";
import _FormControl from "@mui/material/FormControl";
import _Select from "@mui/material/Select";
import _MenuItem from "@mui/material/MenuItem";
import _ListItemText from "@mui/material/ListItemText";
import _Checkbox from "@mui/material/Checkbox";
import _Autocomplete from "@mui/material/Autocomplete";
import _TextField from "@mui/material/TextField";
import _InputAdornment from "@mui/material/InputAdornment";
import _Tooltip from "@mui/material/Tooltip";
import _IconButton from "@mui/material/IconButton";
import _Tabs from "@mui/material/Tabs";
import _Tab from "@mui/material/Tab";
import _Box from "@mui/material/Box";
import _Skeleton from "@mui/material/Skeleton";
import _CircularProgress from "@mui/material/CircularProgress";
import _Typography from "@mui/material/Typography";
function _extends() { return _extends = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends.apply(null, arguments); }
import debounce from 'lodash/debounce';
import moment from 'moment';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useParams, useSearchParams } from 'react-router';
import { useCancellablePromise, useFileSaver } from '../../../hooks';
import { useAmplitudeTracker } from '../../../hooks/tracker';
import { CyclabilityZoneService, EventService } from '../../../services';
import { Button } from '../../button';
import BttfFAQ from '../../event-details/bttf-faq';
import RankCard from '../../event-details/companies-leaderboard/rank-card';
import { useIndividualLeaderboardDownload } from '../../event-details/individual-leaderboard/use-download';
import { ExportIcon, SearchIcon } from '../../icons';
import IndividualLeaderboardList from './individual-list';
import IndividualLeaderboardPodium from './individual-podium';
import LeaderboardList from './list';
import LeaderboardPodium from './podium';
const tabsLabelKeys = {
  group: 'commons.events.details.leaderboard.tabs.group',
  individual: 'commons.events.details.leaderboard.tabs.individual',
  faq: 'commons.events.details.leaderboard.tabs.faq'
};
export const entityTypes = ['company',
// 'companySites',
'association', 'school', 'city'];
const entityTypeToCommunityType = {
  company: 'company',
  association: 'asso',
  school: 'school',
  city: 'city'
};
const entityTypesLabelKeys = {
  company: 'commons.events.details.leaderboard.entity_types.companies',
  // companySites: 'commons.events.details.leaderboard.entity_types.company_sites',
  association: 'commons.events.details.leaderboard.entity_types.associations',
  school: 'commons.events.details.leaderboard.entity_types.schools',
  city: 'commons.events.details.leaderboard.entity_types.territories'
};
const cyclabilityZoneTypeLabelKeys = {
  city: 'commons.events.details.leaderboard.zone_types.city',
  epci: 'commons.events.details.leaderboard.zone_types.epci',
  department: 'commons.events.details.leaderboard.zone_types.department',
  region: 'commons.events.details.leaderboard.zone_types.region'
};
export const sizeRanges = [{
  labelKey: 'commons.events.details.leaderboard.size_ranges.less_than',
  max: 50
}, {
  labelKey: 'commons.events.details.leaderboard.size_ranges.between',
  min: 51,
  max: 100
}, {
  labelKey: 'commons.events.details.leaderboard.size_ranges.between',
  min: 101,
  max: 500
}, {
  labelKey: 'commons.events.details.leaderboard.size_ranges.more_than',
  min: 501
}];
const territorySizeRanges = [{
  labelKey: 'commons.events.details.leaderboard.size_ranges.between',
  min: 0,
  max: 30000
}, {
  labelKey: 'commons.events.details.leaderboard.size_ranges.between',
  min: 30001,
  max: 70000
}, {
  labelKey: 'commons.events.details.leaderboard.size_ranges.between',
  min: 70001,
  max: 300000
}, {
  labelKey: 'commons.events.details.leaderboard.size_ranges.more_than',
  min: 300000
}];
export function EventLeaderboard({
  event,
  currentUser,
  isMember,
  hideTitle = false,
  hideTabs = false,
  holdingGroupId,
  holdingGroupName,
  onlyHoldingGroupCompanies: onlyHoldingGroupCompaniesProp,
  areaId,
  defaultEntityTypes,
  onExportReady,
  focusIndex,
  focusRequestId,
  ownGeogroup
}) {
  const {
    id: eventId
  } = useParams();
  const {
    search: _search
  } = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [viewInitialized, setViewInitialized] = useState(false);
  const [initialized, setInitialized] = useState(false);
  const [selectedTab, selectTab] = useState(() => {
    const tabParam = searchParams.get('leaderboard-tab');
    if (tabParam === 'individual') return tabParam;
    return 'group';
  });
  const availableTabs = useMemo(() => {
    if (!event) return ['group'];
    const _tabs = ['group'];
    if (event.enableIndividualLeaderboard) _tabs.push('individual');
    if (event.challengeTemplate?.type === 'bttf') _tabs.push('faq');
    return _tabs;
  }, [event]);
  const [search, setSearch] = useState(() => {
    const param = searchParams.get('search');
    return param || '';
  });
  const [tmpSearch, setTmpSearch] = useState(search);
  const [cyclabilityZoneSearch, setCyclabilityZoneSearch] = useState('');
  const [cyclabilityZoneSearchResults, setCyclabilityZoneSearchResults] = useState();
  const [selectedZone, selectZone] = useState();
  const [selectedEntityTypes, selectEntityTypes] = useState(() => {
    const ele = searchParams.get('participant-types')?.split(',') || [];
    const initialEntityTypes = [...entityTypes].filter(type => ele.includes(type));
    return initialEntityTypes.length > 0 ? initialEntityTypes : defaultEntityTypes ?? [...entityTypes];
  });
  const [companySitesDisplayed, displayCompanySites] = useState(() => searchParams.get('display-sites') === 'true');
  const [onlyHoldingGroupCompanies, setOnlyHoldingGroupCompanies] = useState(() => {
    if (onlyHoldingGroupCompaniesProp !== undefined) return onlyHoldingGroupCompaniesProp;
    const param = searchParams.get('holding-groups');
    if (param !== null) return param === 'true';
    return !!holdingGroupId;
  });

  // Controlled mode (see `onlyHoldingGroupCompanies` prop doc) — keep internal state in sync with
  // the parent-driven value instead of the (now hidden) checkbox.
  useEffect(() => {
    if (onlyHoldingGroupCompaniesProp !== undefined) setOnlyHoldingGroupCompanies(onlyHoldingGroupCompaniesProp);
  }, [onlyHoldingGroupCompaniesProp]);
  const [sizeRangeIndex, setSizeRangeIndex] = useState(() => {
    const param = searchParams.get('size-range');
    const index = typeof param === 'string' ? parseInt(param) : -1;
    return !isNaN(index) && index >= 0 && index < sizeRanges.length ? index : -1;
  });
  const [pageSize, setPageSize] = useState(50);
  const [page, setPage] = useState(1);
  // While a "voir ma position" jump (see focusRank/focusRequestId below) is in progress, this
  // suppresses the filter-change effect's own "reset to page 1" — otherwise that reset reads
  // `page` before the jump's own page change lands and clobbers it back to 1.
  const suppressPageResetRef = useRef(false);
  const [participantsCount, setParticipantsCount] = useState();
  const [podiumItems, setPodiumItems] = useState();
  const [items, setItems] = useState();
  const [ownItem, setOwnItem] = useState();
  const [individualItems, setIndividualItems] = useState();
  const [downloading, setDownloading] = useState(false);
  const {
    downloading: individualDownloading,
    handleDownload: handleIndividualDownload
  } = useIndividualLeaderboardDownload();
  const locked = useRef(false);
  const fetch = useMemo(() => debounce(async (search, callback) => {
    if (locked.current) {
      callback([]);
      locked.current = false;
      return;
    }
    const {
      zones: cyclabilityZones
    } = await CyclabilityZoneService.getZones({
      administrativeLevels: ['CITY', 'EPCI', 'DEPARTMENT', 'REGION'],
      query: '{id, administrative_level, name, reference}',
      search,
      rowsPerPage: 20,
      getAllItems: false
    });
    callback(cyclabilityZones);
  }, 400), []);
  const {
    t
  } = useTranslation();
  const {
    trackEvent
  } = useAmplitudeTracker();
  const {
    cancellablePromise,
    cancelPromises
  } = useCancellablePromise();
  const {
    cancellablePromise: cancellableViewPromise,
    cancelPromises: cancelViewPromises
  } = useCancellablePromise();
  const {
    downloadXLSX
  } = useFileSaver();
  useEffect(() => {
    setViewInitialized(true);
    return () => cancelViewPromises();
  }, []);
  useEffect(() => {
    if (viewInitialized) {
      const param = searchParams.get('zone');
      const id = param && parseInt(param);
      if (id && !isNaN(id)) {
        getInitialZone(id);
      } else {
        selectZone(null);
      }
    }
  }, [viewInitialized]);
  useEffect(() => {
    if (event && selectedZone !== undefined && isMember !== undefined) {
      if (!isMember) selectTab('group');
      if (event.isOnlyForCompanies) {
        selectEntityTypes(['company']);
      } else if (event.isOnlyForTerritories) {
        selectEntityTypes(['city']);
        displayCompanySites(false);
      }
      if (!event.isForCompanies && event.challengeTemplate?.subGroupDisplay === 'companySite' || event.isOnlyForCompanies && event.allowedSubGroupType === 'companySite') displayCompanySites(true);
      setInitialized(true);
    }
  }, [event, selectedZone, isMember]);
  useEffect(() => {
    if (initialized) {
      if (page === 1) getRanking();else setPage(1);
    }
    return () => cancelPromises();
  }, [event]);
  useEffect(() => {
    let active = true;
    setCyclabilityZoneSearchResults(undefined);
    if (!cyclabilityZoneSearch || cyclabilityZoneSearch.length < 3) return;
    fetch(cyclabilityZoneSearch, cyclabilityZones => {
      if (active) setCyclabilityZoneSearchResults(cyclabilityZones);
    });
    return () => {
      active = false;
    };
  }, [cyclabilityZoneSearch, fetch]);
  useEffect(() => {
    if (!initialized) return;
    if (suppressPageResetRef.current) return;
    if (page > 1) setPage(1);else getRanking();
    const params = new URLSearchParams(window.location.search);
    if (selectedTab === 'group') {
      params.set('participant-types', selectedEntityTypes.join(','));
      if (sizeRangeIndex > -1) params.set('size-range', `${sizeRangeIndex}`);else params.delete('size-range');
      if (selectedZone) params.set('zone', `${selectedZone.id}`);else params.delete('zone');
      if (selectedEntityTypes.includes('company')) params.set('display-sites', companySitesDisplayed ? 'true' : 'false');else params.delete('display-sites');
      params.set('holding-groups', onlyHoldingGroupCompanies ? 'true' : 'false');
      params.delete('leaderboard-tab');
    } else {
      params.set('leaderboard-tab', selectedTab);
    }
    if (search) params.set('search', search);else params.delete('search');
    setSearchParams(params, {
      replace: true
    });
    return () => {
      if (suppressPageResetRef.current) return;
      cancelPromises();
      setParticipantsCount(undefined);
      setPodiumItems(undefined);
      setItems(undefined);
      setOwnItem(undefined);
      setIndividualItems(undefined);
    };
  }, [initialized, selectedTab, search, selectedEntityTypes, companySitesDisplayed, onlyHoldingGroupCompanies, sizeRangeIndex, selectedZone, pageSize, areaId]);
  useEffect(() => {
    return () => {
      const params = new URLSearchParams(window.location.search);
      ['participant-types', 'size-range', 'zone', 'display-sites', 'holding-groups', 'leaderboard-tab', 'search'].forEach(key => params.delete(key));
      setSearchParams(params, {
        replace: true
      });
    };
  }, []);
  useEffect(() => {
    if (!initialized) return;
    if (suppressPageResetRef.current) return;
    getRanking();
    const ele = document.getElementById('leaderboard');
    if (ele) {
      window.scrollTo({
        top: ele.offsetTop - 48,
        left: 0,
        behavior: 'smooth'
      });
    }
    return () => {
      if (suppressPageResetRef.current) return;
      cancelPromises();
      setItems(undefined);
      setIndividualItems(undefined);
    };
  }, [page]);
  const shouldDisplaySizeFilter = selectedEntityTypes.length === 1;
  const selectedEntityType = selectedEntityTypes.length === 1 ? selectedEntityTypes[0] : null;
  const activeSizeRanges = selectedEntityType === 'city' ? territorySizeRanges : sizeRanges;
  const lastHandledFocusRequestId = useRef(undefined);
  const focusSearchTokenRef = useRef(0);
  const ownItemIndex = ownGeogroup ? items?.findIndex(item => item.id === ownGeogroup.id) : -1;
  const highlightIndex = ownItemIndex !== undefined && ownItemIndex > -1 ? ownItemIndex : undefined;
  useEffect(() => {
    if (!focusRequestId || focusRequestId === lastHandledFocusRequestId.current || focusIndex === undefined || !initialized || !event || !ownGeogroup) {
      return;
    }
    lastHandledFocusRequestId.current = focusRequestId;
    const token = ++focusSearchTokenRef.current;
    selectTab('group');
    setTmpSearch('');
    setSearch('');
    selectZone(null);
    setSizeRangeIndex(-1);
    selectEntityTypes(['company']);
    displayCompanySites(false);
    setOnlyHoldingGroupCompanies(false);
    runFocusSearch(token, focusIndex, event.id, ownGeogroup.id);
  }, [focusRequestId, initialized, focusIndex, event, ownGeogroup]);
  async function runFocusSearch(token, leaderboardIndex, eventId, ownGeogroupId) {
    suppressPageResetRef.current = true;
    try {
      cancelPromises();
      let currentPage = Math.floor(leaderboardIndex / pageSize) + 1;
      let safety = 0;
      if (currentPage !== 1) {
        cancellablePromise(EventService.getRanking(eventId, {
          page: 1,
          pageSize: 3,
          groupTypes: ['company']
        })).then(({
          items: podiumItemsResult
        }) => {
          setPodiumItems(podiumItemsResult);
        }).catch(err => {
          if (!(err instanceof Error) || err?.name !== 'CancelledPromiseError') {
            console.error(err);
          }
        });
      }
      for (;;) {
        safety += 1;
        if (safety > 50) {
          return;
        }
        if (token !== focusSearchTokenRef.current) {
          return;
        }
        const result = await cancellablePromise(EventService.getRanking(eventId, {
          page: currentPage,
          pageSize,
          groupTypes: ['company']
        }));
        if (token !== focusSearchTokenRef.current) {
          return;
        }
        const {
          count,
          items: pageItems
        } = result;
        setParticipantsCount(count);
        setPage(currentPage);
        setItems(pageItems);
        if (currentPage === 1) setPodiumItems(pageItems.slice(0, 3));
        const expectedOffset = leaderboardIndex - (currentPage - 1) * pageSize;
        const foundIndex = pageItems[expectedOffset]?.id === ownGeogroupId ? expectedOffset : pageItems.findIndex(item => item.id === ownGeogroupId);
        if (foundIndex !== -1) {
          const elementId = `leaderboard-item-${foundIndex}`;
          requestAnimationFrame(() => {
            const element = document.getElementById(elementId);
            element?.scrollIntoView({
              behavior: 'smooth',
              block: 'center'
            });
          });
          return;
        }
        if (currentPage * pageSize >= count) {
          return;
        }
        currentPage += 1;
      }
    } catch (err) {
      if (!(err instanceof Error) || err?.name !== 'CancelledPromiseError') console.error(err);
    } finally {
      suppressPageResetRef.current = false;
    }
  }
  useEffect(() => {
    if (!initialized || shouldDisplaySizeFilter || sizeRangeIndex === -1) return;
    setSizeRangeIndex(-1);
  }, [initialized, shouldDisplaySizeFilter, sizeRangeIndex]);
  async function getInitialZone(id) {
    try {
      const _zone = await CyclabilityZoneService.getZone(id, {
        query: '{ id, code, name, administrative_level, reference }'
      });
      const zone = ['region', 'department', 'epci', 'city'].includes(_zone.administrativeLevel) ? _zone : null;
      if (zone) {
        selectZone(zone);
        setCyclabilityZoneSearchResults([zone]);
      } else {
        selectZone(null);
      }
    } catch (err) {
      console.error(err);
      selectZone(null);
    }
  }
  async function getRanking() {
    if (!event) return;
    try {
      if (selectedTab === 'group') {
        const groupTypes = selectedEntityTypes.length === entityTypes.length ? [] : selectedEntityTypes;
        const size = sizeRangeIndex > -1 ? activeSizeRanges[sizeRangeIndex] : undefined;
        const holdingGroupIdFilter = onlyHoldingGroupCompanies ? holdingGroupId : null;
        const fetchOwnItem = page === 1 && ownGeogroup && selectedEntityTypes.includes('company');
        const [{
          count,
          items
        }, ownItemResult] = await cancellablePromise(Promise.all([EventService.getRanking(event.id, {
          page,
          pageSize,
          groupTypes,
          size,
          cyclabilityZoneId: selectedZone?.id,
          areaId,
          search,
          holdingGroupId: holdingGroupIdFilter,
          showSites: companySitesDisplayed
        }), fetchOwnItem ? EventService.getRanking(event.id, {
          page: 1,
          pageSize: 1,
          groupTypes,
          size,
          cyclabilityZoneId: selectedZone?.id,
          areaId,
          search,
          holdingGroupId: holdingGroupIdFilter,
          showSites: companySitesDisplayed,
          groupsIds: [ownGeogroup.id]
        }) : undefined]));
        if (page === 1) {
          setPodiumItems(items.slice(0, 3));
          if (fetchOwnItem) setOwnItem(ownItemResult?.items[0] || null);else setOwnItem(null);
        }
        setItems(items);
        setParticipantsCount(count);
      } else {
        if (!isMember) return;
        const {
          count,
          items
        } = await cancellablePromise(EventService.getIndividualRanking(event.id, {
          page,
          pageSize,
          search
        }));
        setIndividualItems(items);
        setParticipantsCount(count);
      }
    } catch (err) {
      if (!(err instanceof Error) || err?.name !== 'CancelledPromiseError') {
        console.error(err);
      }
    }
  }
  async function handleDownload() {
    if (!event?.challengeTemplate || !participantsCount) return;
    setDownloading(true);
    try {
      const nbPages = Math.ceil(participantsCount / 100);
      let progressValueHeader;
      let averagePerParticipantHeader = t('commons.events.details.leaderboard.average_per_participant');
      if (event.challengeTemplate.type === 'cyclingDays') {
        progressValueHeader = t('commons.challenges.types.cycling_days');
        averagePerParticipantHeader += ` (${t('commons.challenges.types.cycling_days').toLocaleLowerCase()})`;
      } else if (event.challengeTemplate.type === 'traveledDistance') {
        progressValueHeader = `${t('commons.distance')} (km)`;
        averagePerParticipantHeader += ' (km)';
      } else {
        progressValueHeader = t('commons.challenges.types.journeys');
        averagePerParticipantHeader += ` (${t('commons.challenges.types.journeys')})`;
      }
      const onlyCityGroups = selectedEntityTypes.length === 1 && selectedEntityTypes[0] === 'city';
      if (selectedTab === 'group') {
        const allItems = (await cancellableViewPromise(Promise.all(new Array(nbPages).fill(null).map((_, index) => EventService.getRanking(event.id, {
          page: index + 1,
          pageSize: 100,
          groupTypes: selectedEntityTypes.length === entityTypes.length ? [] : selectedEntityTypes,
          size: sizeRangeIndex > -1 ? activeSizeRanges[sizeRangeIndex] : undefined,
          cyclabilityZoneId: selectedZone?.id,
          areaId,
          search,
          holdingGroupId: onlyHoldingGroupCompanies ? holdingGroupId : null,
          showSites: companySitesDisplayed
        }))))).flatMap(({
          items
        }) => items);
        downloadXLSX(`${[t('commons.events.details.leaderboard.title'), event.title].join('_').normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/\s+/g, '_')}_${moment().format('YYYY-MM-DD')}.xlsx`, [t('commons.events.details.leaderboard.rank'), t('commons.events.details.leaderboard.group'), 'Taille', t('commons.events.details.leaderboard.members_count'), progressValueHeader, ...(onlyCityGroups ? [] : [averagePerParticipantHeader])], allItems.map(({
          rank,
          parentTitle,
          title,
          progressValue,
          activeParticipantsCount,
          sizeRange,
          type
        }) => [rank, parentTitle ? parentTitle : title, sizeRange ? `${sizeRange.min}-${sizeRange.max}` : '', activeParticipantsCount, event.challengeTemplate?.type === 'traveledDistance' ? Math.round(progressValue / 10) / 100 : progressValue, ...(type !== 'city' ? [Math.round((activeParticipantsCount ? progressValue / activeParticipantsCount : 0) / 10) / 100] : [])]));
      } else {
        await handleIndividualDownload(event, participantsCount, search);
      }
    } catch (err) {
      if (!(err instanceof Error) || err?.name !== 'CancelledPromiseError') {
        console.error(err);
      }
    }
    setDownloading(false);
  }
  function resetSearch() {
    setTmpSearch('');
    setSearch('');
  }
  useEffect(() => {
    if (!onExportReady) return;
    onExportReady(event && participantsCount ? handleDownload : undefined);
    return () => onExportReady(undefined);
  }, [onExportReady, event, participantsCount, selectedTab]);
  return /*#__PURE__*/React.createElement(_Box, {
    display: "flex",
    flexDirection: "column",
    gap: 3,
    width: "100%"
  }, !onExportReady && /*#__PURE__*/React.createElement(_Box, {
    alignItems: "center",
    display: "flex",
    gap: 3,
    justifyContent: "space-between"
  }, /*#__PURE__*/React.createElement(_Typography, {
    component: "h2",
    fontWeight: 700,
    variant: "h5"
  }, hideTitle ? '' : t('commons.events.details.leaderboard.title')), /*#__PURE__*/React.createElement(_Box, {
    flexShrink: 0
  }, event && participantsCount !== undefined ? participantsCount ? /*#__PURE__*/React.createElement(Button, {
    color: "primary",
    disabled: downloading || individualDownloading,
    onClick: handleDownload,
    startIcon: downloading || individualDownloading ? /*#__PURE__*/React.createElement(_CircularProgress, {
      color: "inherit",
      size: 16,
      thickness: 4
    }) : /*#__PURE__*/React.createElement(ExportIcon, null),
    variant: "text"
  }, t('commons.actions.export')) : /*#__PURE__*/React.createElement(React.Fragment, null) : /*#__PURE__*/React.createElement(_Skeleton, {
    height: 36,
    variant: "rounded",
    width: 100
  }))), isMember && availableTabs.length > 1 && !hideTabs && /*#__PURE__*/React.createElement(_Tabs, {
    onChange: (_, tab) => selectTab(tab),
    value: selectedTab
  }, availableTabs.map(key => /*#__PURE__*/React.createElement(_Tab, {
    key: key,
    label: key === 'group' && event?.isOnlyForCompanies ? t('commons.events.details.leaderboard.tabs.company') : t(tabsLabelKeys[key]),
    sx: {
      minHeight: 48,
      textTransform: 'initial'
    },
    value: key
  }))), selectedTab === 'group' && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(_Box, {
    display: "flex",
    flexDirection: "column",
    gap: 2
  }, /*#__PURE__*/React.createElement(_Box, {
    alignItems: "center",
    columnGap: 3,
    display: "flex",
    flexWrap: "wrap",
    rowGap: 2
  }, /*#__PURE__*/React.createElement(_Box, {
    width: {
      xs: '100%',
      md: event?.allowGeographicFilter ? 'calc(100% - (100% - 48px) / 3 - 24px)' : 'calc(100% - 24px)'
    }
  }, initialized ? /*#__PURE__*/React.createElement(_TextField, {
    fullWidth: true,
    margin: "none",
    onChange: ({
      currentTarget: {
        value
      }
    }) => setTmpSearch(value),
    onKeyDown: ({
      key
    }) => {
      if (key === 'Enter') setSearch(tmpSearch);
    },
    placeholder: t('commons.events.details.leaderboard.actions.search', {
      context: event?.isOnlyForCompanies ? 'company' : ''
    }) || '',
    size: "small",
    slotProps: {
      input: {
        endAdornment: tmpSearch && /*#__PURE__*/React.createElement(_InputAdornment, {
          position: "end"
        }, /*#__PURE__*/React.createElement(_Tooltip, {
          title: "Rechercher"
        }, /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement(_IconButton, {
          disabled: !tmpSearch,
          onClick: () => setSearch(tmpSearch),
          size: "small"
        }, /*#__PURE__*/React.createElement(SearchIcon, {
          fontSize: "small"
        })))))
      }
    },
    type: "text",
    value: tmpSearch,
    variant: "outlined"
  }) : /*#__PURE__*/React.createElement(_Skeleton, {
    height: 40,
    variant: "rounded",
    width: "100%"
  })), event?.allowGeographicFilter && areaId == null && /*#__PURE__*/React.createElement(_Box, {
    width: {
      xs: '100%',
      md: 'calc((100% - 48px) / 3)'
    }
  }, initialized ? /*#__PURE__*/React.createElement(_Autocomplete, {
    disablePortal: true,
    includeInputInList: true,
    forcePopupIcon: Boolean(cyclabilityZoneSearch && !selectedZone),
    getOptionKey: option => option.id,
    getOptionLabel: option => option.name,
    isOptionEqualToValue: (option, value) => option.id === value.id,
    loading: Boolean(cyclabilityZoneSearch) && !selectedZone && cyclabilityZoneSearchResults === undefined,
    loadingText: t('commons.loading'),
    noOptionsText: cyclabilityZoneSearch && t('commons.no_result_found'),
    onChange: (_, value) => {
      locked.current = true;
      selectZone(value || null);
    },
    onInputChange: (_, value) => setTimeout(() => setCyclabilityZoneSearch(value), 10),
    options: cyclabilityZoneSearchResults || [],
    renderInput: params => /*#__PURE__*/React.createElement(_TextField, _extends({}, params, {
      fullWidth: true,
      margin: "none",
      placeholder: t('commons.challenges.leaderboard.search_zone'),
      size: "small",
      variant: "outlined"
    })),
    renderOption: (props, option) => {
      const typeLabelKey = cyclabilityZoneTypeLabelKeys[option.administrativeLevel];
      const typeLabel = typeLabelKey ? t(typeLabelKey) : '';
      return /*#__PURE__*/React.createElement(_Box, _extends({}, props, {
        component: "li",
        display: "flex",
        justifyContent: "space-between",
        width: "100%"
      }), /*#__PURE__*/React.createElement(_Typography, {
        noWrap: true,
        variant: "body2"
      }, option.administrativeLevel === 'city' && option.ref ? `${option.name} (${option.ref.substring(0, 2)})` : option.name), typeLabel && /*#__PURE__*/React.createElement(_Typography, {
        color: "text.secondary",
        flexShrink: 0,
        fontSize: 11,
        marginLeft: 1,
        textTransform: "uppercase",
        variant: "caption"
      }, typeLabel));
    },
    value: selectedZone
  }) : /*#__PURE__*/React.createElement(_Skeleton, {
    height: 40,
    variant: "rounded",
    width: "100%"
  }))), /*#__PURE__*/React.createElement(_Box, {
    alignItems: "center",
    columnGap: 3,
    display: "flex",
    flexWrap: "wrap",
    rowGap: 2
  }, initialized ? event && !event.isOnlyForCompanies && !event.isOnlyForTerritories && /*#__PURE__*/React.createElement(_Box, {
    width: {
      xs: '100%',
      md: 'calc((100% - 48px) / 3)'
    }
  }, /*#__PURE__*/React.createElement(_FormControl, {
    fullWidth: true,
    margin: "none"
  }, /*#__PURE__*/React.createElement(_Select, {
    displayEmpty: true,
    multiple: true,
    onChange: ({
      target: {
        value
      }
    }) => {
      const isAll = value === 'all' || !(typeof value === 'string') && value.indexOf('all') > -1;
      const newSelectedEntityTypes = isAll ? [...entityTypes] : typeof value === 'string' ? value.split(',') : value;
      if (isAll || newSelectedEntityTypes.length > 0) {
        selectEntityTypes(newSelectedEntityTypes);
        if (!isAll && !newSelectedEntityTypes.includes('company')) displayCompanySites(false);
        trackEvent('Event filter type changed', {
          community_type: newSelectedEntityTypes.map(t => entityTypeToCommunityType[t]).join(',')
        });
      }
      resetSearch();
    },
    renderValue: selected => {
      if (selected.length === 0 || selected.length === entityTypes.length) return /*#__PURE__*/React.createElement(_Typography, {
        color: "#b6b6b6",
        component: "span",
        fontSize: "inherit"
      }, t('commons.events.details.leaderboard.all_entity_types'));
      return selected.map(key => t(entityTypesLabelKeys[key])).join(', ');
    },
    size: "small",
    value: selectedEntityTypes
  }, /*#__PURE__*/React.createElement(_MenuItem, {
    value: "all"
  }, /*#__PURE__*/React.createElement(_Checkbox, {
    checked: selectedEntityTypes.length === entityTypes.length,
    indeterminate: selectedEntityTypes.length !== entityTypes.length,
    sx: {
      marginRight: 1.5,
      padding: 0.5
    }
  }), /*#__PURE__*/React.createElement(_ListItemText, {
    primary: t('commons.events.details.leaderboard.all_entity_types')
  })), entityTypes.map(key => /*#__PURE__*/React.createElement(_MenuItem, {
    key: key,
    value: key
  }, /*#__PURE__*/React.createElement(_Checkbox, {
    checked: selectedEntityTypes.indexOf(key) > -1,
    sx: {
      marginRight: 1.5,
      padding: 0.5
    }
  }), /*#__PURE__*/React.createElement(_ListItemText, {
    primary: t(entityTypesLabelKeys[key])
  })))))) : /*#__PURE__*/React.createElement(_Box, {
    width: {
      xs: '100%',
      md: 'calc((100% - 48px) / 3)'
    }
  }, /*#__PURE__*/React.createElement(_Skeleton, {
    height: 40,
    variant: "rounded",
    width: "100%"
  })), shouldDisplaySizeFilter && /*#__PURE__*/React.createElement(_Box, {
    width: {
      xs: '100%',
      md: 'calc((100% - 48px) / 3)'
    }
  }, initialized ? /*#__PURE__*/React.createElement(_FormControl, {
    fullWidth: true,
    margin: "none"
  }, /*#__PURE__*/React.createElement(_Select, {
    onChange: ({
      target: {
        value
      }
    }) => {
      setSizeRangeIndex(typeof value === 'string' ? parseInt(value) : value);
      resetSearch();
    },
    renderValue: selected => {
      if (selected === -1) return /*#__PURE__*/React.createElement(_Typography, {
        color: "#b6b6b6",
        component: "span",
        fontSize: "inherit"
      }, t('commons.events.details.leaderboard.all_sizes'));
      const {
        labelKey,
        min,
        max
      } = activeSizeRanges[selected];
      return t(labelKey, {
        min,
        max
      });
    },
    size: "small",
    value: sizeRangeIndex
  }, /*#__PURE__*/React.createElement(_MenuItem, {
    value: -1
  }, t('commons.events.details.leaderboard.all_sizes')), activeSizeRanges.map(({
    labelKey,
    min,
    max
  }, index) => /*#__PURE__*/React.createElement(_MenuItem, {
    key: index,
    value: index
  }, t(labelKey, {
    min,
    max
  }))))) : /*#__PURE__*/React.createElement(_Skeleton, {
    height: 40,
    variant: "rounded",
    width: "100%"
  })), event && event.allowCompanySiteFilter ? initialized ? selectedEntityTypes.includes('company') && /*#__PURE__*/React.createElement(_Box, {
    width: {
      xs: '100%',
      md: 'calc((100% - 48px) / 3)'
    }
  }, /*#__PURE__*/React.createElement(_FormControlLabel, {
    control: /*#__PURE__*/React.createElement(_Checkbox, {
      checked: companySitesDisplayed,
      color: "secondary",
      disabled: onlyHoldingGroupCompanies,
      onChange: (_, checked) => displayCompanySites(checked),
      size: "small",
      value: companySitesDisplayed
    }),
    label: t('commons.events.details.leaderboard.display_company_sites'),
    slotProps: {
      typography: {
        lineHeight: 1,
        variant: 'body1'
      }
    }
  })) : /*#__PURE__*/React.createElement(_Box, {
    width: {
      xs: '100%',
      md: 'calc((100% - 48px) / 3)'
    }
  }, /*#__PURE__*/React.createElement(_Skeleton, {
    height: 40,
    variant: "rounded",
    width: "100%"
  })) : /*#__PURE__*/React.createElement(React.Fragment, null)), holdingGroupId && onlyHoldingGroupCompaniesProp === undefined && /*#__PURE__*/React.createElement(_Box, null, /*#__PURE__*/React.createElement(_Box, {
    width: {
      xs: '100%',
      md: '100%'
    }
  }, /*#__PURE__*/React.createElement(_FormControlLabel, {
    control: /*#__PURE__*/React.createElement(_Checkbox, {
      checked: onlyHoldingGroupCompanies,
      color: "secondary",
      onChange: (_, checked) => {
        if (companySitesDisplayed && checked) {
          displayCompanySites(false);
        }
        setOnlyHoldingGroupCompanies(checked);
        trackEvent('Event holding toggle clicked', {
          is_enabled: checked
        });
      },
      size: "small",
      value: onlyHoldingGroupCompanies
    }),
    label: t('commons.events.details.leaderboard.display_only_holding_groups', {
      holdingName: holdingGroupName || ''
    }),
    slotProps: {
      typography: {
        lineHeight: 1,
        variant: 'body1'
      }
    }
  })))), /*#__PURE__*/React.createElement(LeaderboardPodium, {
    event: event,
    items: podiumItems
  }), ownGeogroup && selectedEntityTypes.includes('company') && /*#__PURE__*/React.createElement(RankCard, {
    challengeTemplate: event?.challengeTemplate || null,
    numberOfParticipants: participantsCount,
    progress: ownItem,
    withoutPointsUnit: event?.challengeTemplate?.progressWeightingMode === 'EMPLOYEE_RATIO'
  }), /*#__PURE__*/React.createElement(LeaderboardList, {
    event: event,
    highlightIndex: highlightIndex,
    items: items,
    page: page,
    pageSize: pageSize,
    participantsCount: participantsCount,
    setPage: setPage,
    setPageSize: setPageSize
  })), selectedTab === 'individual' && (!currentUser ? /*#__PURE__*/React.createElement(_Box, {
    alignItems: "center",
    display: "flex",
    flexDirection: "column",
    gap: 3,
    paddingY: 10
  }, /*#__PURE__*/React.createElement(_AccountCircleOutlined, {
    color: "action",
    sx: {
      height: 100,
      width: 100
    }
  }), /*#__PURE__*/React.createElement(_Typography, {
    align: "center",
    color: "textSecondary"
  }, t('commons.events.details.leaderboard.individual_guest')), /*#__PURE__*/React.createElement(Button, {
    component: Link,
    disabled: currentUser === undefined,
    size: "large",
    state: {
      requestedPath: `/${eventId}${_search}`
    },
    to: "/sign-in",
    variant: "outlined"
  }, t('commons.events.navigation.sign_in'))) : /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(_Box, null, initialized ? /*#__PURE__*/React.createElement(_TextField, {
    fullWidth: true,
    margin: "none",
    onChange: ({
      currentTarget: {
        value
      }
    }) => setTmpSearch(value),
    onKeyDown: ({
      key
    }) => {
      if (key === 'Enter') setSearch(tmpSearch);
    },
    placeholder: t('commons.events.details.leaderboard.actions.search_participant'),
    size: "small",
    slotProps: {
      input: {
        endAdornment: tmpSearch && /*#__PURE__*/React.createElement(_InputAdornment, {
          position: "end"
        }, /*#__PURE__*/React.createElement(_Tooltip, {
          title: "Rechercher"
        }, /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement(_IconButton, {
          disabled: !tmpSearch,
          onClick: () => setSearch(tmpSearch),
          size: "small"
        }, /*#__PURE__*/React.createElement(SearchIcon, {
          fontSize: "small"
        })))))
      }
    },
    type: "text",
    value: tmpSearch,
    variant: "outlined"
  }) : /*#__PURE__*/React.createElement(_Skeleton, {
    height: 40,
    variant: "rounded",
    width: "100%"
  })), /*#__PURE__*/React.createElement(IndividualLeaderboardPodium, {
    event: event,
    items: individualItems
  }), /*#__PURE__*/React.createElement(IndividualLeaderboardList, {
    event: event,
    items: individualItems,
    page: page,
    pageSize: pageSize,
    participantsCount: participantsCount,
    setPage: setPage,
    setPageSize: setPageSize
  }))), selectedTab === 'faq' && /*#__PURE__*/React.createElement(_Box, null, /*#__PURE__*/React.createElement(BttfFAQ, {
    padding: true
  })));
}